import { Drawer } from '@material-tailwind/react';
import { BeatLoader } from 'react-spinners';
import { IoMdClose, IoIosArrowForward } from 'react-icons/io';

export default function UserReportChatModel({
    chatRoomData,
    userReportStep,
    userReportReason,
    userReportSubReason,
    setUserReportSubReason,
    userReportDetails,
    setUserReportDetails,
    userReportBlockUser,
    setUserReportBlockUser,
    userReportError,
    userReportSuccess,
    handleSubmitUserReport,
    userReportLoading,
    handleCancelUserReport,
    handleBackToUserReportReasons,
    reportReasons,
    reportReasonsLoading,
    handleUserReasonSelect,
}) {
    const isSubmitDisabled = userReportLoading || !userReportReason || (userReportReason?.sub_reasons?.length > 0 && !userReportSubReason);
    const userReportDrawerSize = userReportSuccess ? 220 : 540;

    return (
        <Drawer
            size={userReportDrawerSize}
            placement="bottom"
            open={true}
            onClose={handleCancelUserReport}
            className="absolute w-full rounded-t-2xl bg-white dark:bg-gray-900 max-h-[90vh] shadow-2xl"
            style={{ zIndex: 50 }}
        >
            <div className={`relative flex flex-col ${userReportSuccess ? "h-auto max-h-[70vh]" : "h-full max-h-[80vh]"}`}>
                <div className="relative flex items-center justify-center px-4 pt-3 pb-2">
                    {userReportStep === "detail" && !userReportSuccess && (
                        <IoIosArrowForward
                            onClick={handleBackToUserReportReasons}
                            className="absolute left-4 top-4 cursor-pointer rotate-180 text-[20px] text-gray-800 dark:text-gray-100"
                        />
                    )}
                    {userReportStep !== "detail" &&
                        <IoMdClose
                            onClick={handleCancelUserReport}
                            className="absolute right-4 top-3 cursor-pointer text-[20px] text-gray-800 dark:text-gray-100"
                        />
                    }
                </div>

                {!userReportSuccess && userReportStep === "reason" && (
                    <div className="flex h-full flex-col overflow-y-auto pb-6">
                        <div className="px-4 pt-1 pb-4 text-center space-y-1">
                            <h2 className="text-[16px] font-semibold text-black dark:text-white">
                                Report {chatRoomData?.users?.[0]?.full_name}
                            </h2>
                            <p className="text-[11px] text-gray-600 dark:text-gray-300">
                                Reports are confidential. This person won't know you reported them.
                            </p>
                        </div>

                        <div className="border-t border-b border-gray-200 dark:border-gray-800 divide-y divide-gray-200 dark:divide-gray-800">
                            {reportReasonsLoading ? (
                                <div className="flex justify-center items-center py-8">
                                    <BeatLoader size={8} color={"#6b7280"} />
                                </div>
                            ) : (
                                reportReasons.map((item) => (
                                    <button
                                        key={item.code}
                                        type="button"
                                        onClick={() => handleUserReasonSelect(item)}
                                        className="flex w-full items-center justify-between px-5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                                    >
                                        <span className="text-[12px] font-medium text-gray-900 dark:text-gray-100">{item.label}</span>
                                        <IoIosArrowForward className="text-[16px] text-gray-400" />
                                    </button>
                                ))
                            )}
                        </div>

                        <p className="mt-4 px-6 text-center text-[11px] leading-5 text-gray-500 dark:text-gray-400">
                            Learn more about our <span className="text-blue-500 underline cursor-pointer">Community Guidelines</span>.
                        </p>
                    </div>
                )}

                {!userReportSuccess && userReportStep === "detail" && userReportReason && (
                    <div className="flex h-full flex-col overflow-y-auto pb-4">
                        <div className="px-4 pt-2 pb-3 text-center space-y-1">
                            <h3 className="text-[14px] font-semibold text-black dark:text-white">{userReportReason.label}</h3>
                            <p className="text-[12px] text-gray-600 dark:text-gray-300">
                                Tell us more so we can review this report.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 px-4">
                            {userReportReason.sub_reasons?.length > 0 ? (
                                <div className="overflow-hidden rounded-xl border border-gray-500 dark:border-gray-800 divide-y divide-gray-500 dark:divide-gray-800 bg-white dark:bg-gray-900">
                                    {userReportReason.sub_reasons.map((sub) => (
                                        <button
                                            key={sub.code}
                                            type="button"
                                            onClick={() => setUserReportSubReason(sub.code)}
                                            className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                                        >
                                            <span className="text-[12px] text-gray-900 dark:text-gray-100">{sub.label}</span>
                                            <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${userReportSubReason === sub.code ? "border-gray-900 dark:border-gray-100" : "border-gray-500 dark:border-gray-700"}`}>
                                                <span className={`h-2 w-2 rounded-full ${userReportSubReason === sub.code ? "bg-gray-900 dark:bg-gray-100" : "bg-transparent"}`} />
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 p-3 text-[12px] text-gray-600 dark:bg-gray-800 dark:text-gray-200">
                                    No additional categories for this reason. Add optional context below.
                                </div>
                            )}

                            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3">
                                <textarea
                                    value={userReportDetails}
                                    maxLength={250}
                                    placeholder="Share anything that might help us understand the issue (optional)"
                                    onChange={(e) => setUserReportDetails(e.target.value)}
                                    className="h-[90px] w-full resize-none bg-transparent text-[13px] text-gray-900 placeholder-gray-500 outline-none dark:text-gray-100 dark:placeholder-gray-400"
                                />
                                <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                                    {userReportError && <span className="text-red-600">{userReportError}</span>}
                                    <span>{userReportDetails.length}/250</span>
                                </div>
                            </div>

                            <label className="flex cursor-pointer items-center justify-between rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2.5">
                                <span className="text-[12px] font-medium text-gray-900 dark:text-gray-100">
                                    Also block {chatRoomData?.users?.[0]?.full_name}
                                </span>
                                <input
                                    type="checkbox"
                                    checked={userReportBlockUser}
                                    onChange={(e) => setUserReportBlockUser(e.target.checked)}
                                    className="h-4 w-4 accent-red-600 cursor-pointer"
                                />
                            </label>
                        </div>

                        <div className="mt-auto flex gap-3 px-4 pt-4 pb-3">
                            <button
                                type="button"
                                onClick={handleCancelUserReport}
                                className="flex-1 rounded-xl border border-gray-300 bg-white py-2 text-[14px] font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={isSubmitDisabled}
                                onClick={handleSubmitUserReport}
                                className={`flex flex-1 items-center justify-center rounded-xl py-2 text-[14px] font-semibold transition ${isSubmitDisabled ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-gray-800 dark:text-gray-500" : "bg-red-600 text-white hover:bg-red-700"}`}
                            >
                                {userReportLoading ? <BeatLoader size={10} color={"#fff"} loading={userReportLoading} /> : "Submit report"}
                            </button>
                        </div>

                        <p className="px-6 text-center text-[11px] text-gray-500 dark:text-gray-400">
                            Reports are confidential. This person won't know you reported them.
                        </p>
                    </div>
                )}

                {userReportSuccess && (
                    <div className="flex flex-col items-center justify-center gap-4 px-6 pb-6 pt-2 text-center">
                        <p className="text-[20px] font-semibold text-gray-900 dark:text-white">
                            Thanks for letting us know
                        </p>
                        <p className="text-[11px] text-gray-700 dark:text-gray-300">
                            We'll review this report and take action if it violates our guidelines.
                            {userReportBlockUser ? " You've also blocked this user." : " You may not hear back, but your report helps keep the community safe."}
                        </p>
                        <button
                            onClick={handleCancelUserReport}
                            className="w-full bg-gray-800 text-white rounded-xl py-2"
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        </Drawer>
    )
}
